/**
 * AnalyticsLogger - Debug logging for analytics system
 * 
 * Provides structured logging for analytics events, errors, and warnings
 * with support for debug mode to help developers verify analytics implementation.
 */

export class AnalyticsLogger {
  private debugMode: boolean;

  constructor(debugMode: boolean = false) {
    this.debugMode = debugMode;
  }

  /**
   * Enable or disable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Get current debug mode status
   */
  isDebugMode(): boolean {
    return this.debugMode;
  }

  /**
   * Log an analytics event (only in debug mode)
   */
  logEvent(eventName: string, eventData: any): void {
    if (!this.debugMode) {
      return;
    }

    console.log(
      '%c[Analytics Event]',
      'color: #4CAF50; font-weight: bold',
      eventName,
      this.formatEventData(eventData)
    );
  }

  /**
   * Log an error
   */
  logError(error: Error | string, context: string): void {
    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;

    console.error(
      '%c[Analytics Error]',
      'color: #f44336; font-weight: bold',
      context,
      errorMessage
    );

    if (errorStack && this.debugMode) {
      console.error('Stack trace:', errorStack);
    }
  }

  /**
   * Log a warning
   */
  logWarning(message: string, context?: string): void {
    if (context) {
      console.warn(
        '%c[Analytics Warning]',
        'color: #FF9800; font-weight: bold',
        context,
        message
      );
    } else {
      console.warn(
        '%c[Analytics Warning]',
        'color: #FF9800; font-weight: bold',
        message
      );
    }
  }

  /**
   * Log an info message (only in debug mode)
   */
  logInfo(message: string, data?: any): void {
    if (!this.debugMode) {
      return;
    }

    if (data !== undefined) {
      console.log(
        '%c[Analytics Info]',
        'color: #2196F3; font-weight: bold',
        message,
        data
      );
    } else {
      console.log(
        '%c[Analytics Info]',
        'color: #2196F3; font-weight: bold',
        message
      );
    }
  }

  /**
   * Log initialization status
   */
  logInitialization(success: boolean, message: string): void {
    if (success) {
      if (this.debugMode) {
        console.log(
          '%c[Analytics Init]',
          'color: #4CAF50; font-weight: bold',
          '✓',
          message
        );
      }
    } else {
      console.error(
        '%c[Analytics Init]',
        'color: #f44336; font-weight: bold',
        '✗',
        message
      );
    }
  }

  /**
   * Log privacy-related actions
   */
  logPrivacy(action: string, details?: any): void {
    if (this.debugMode) {
      console.log(
        '%c[Analytics Privacy]',
        'color: #9C27B0; font-weight: bold',
        action,
        details || ''
      );
    }
  }

  /**
   * Log event processing actions (debounce, throttle, batch)
   */
  logProcessing(action: string, eventName: string, details?: any): void {
    if (!this.debugMode) {
      return;
    }

    console.log(
      '%c[Analytics Processing]',
      'color: #00BCD4; font-weight: bold',
      action,
      eventName,
      details || ''
    );
  }

  /**
   * Log health metrics
   */
  logHealth(metrics: any): void {
    if (!this.debugMode) {
      return;
    }

    console.log(
      '%c[Analytics Health]',
      'color: #8BC34A; font-weight: bold',
      metrics
    );
  }

  /**
   * Format event data for logging
   */
  private formatEventData(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    // Create a shallow copy to avoid modifying original
    const formatted = { ...data };

    // Truncate long strings for readability
    for (const [key, value] of Object.entries(formatted)) {
      if (typeof value === 'string' && value.length > 100) {
        formatted[key] = value.substring(0, 100) + '...';
      }
    }

    return formatted;
  }
}
